import React from "react";
import { Rocket, Copy, Download, Server, CheckCircle2, Cloud } from "lucide-react";
import { toast } from "sonner";
import { http, downloadJSON, copyText } from "../../lib/api";
import { EMERGENT_PROMPT, AWS_MAP } from "../../lib/constants";
import { Panel, Btn, Overline } from "../ui";

const STATE_COLOR = {
  provisioned: "text-aegis-cyan border-aegis-cyan/40 bg-aegis-cyan/10",
  bound: "text-aegis-cyan border-aegis-cyan/40 bg-aegis-cyan/10",
  active: "text-aegis-emerald border-aegis-emerald/40 bg-aegis-emerald/10",
  enforcing: "text-aegis-amber border-aegis-amber/40 bg-aegis-amber/10",
  live: "text-aegis-emerald border-aegis-emerald/40 bg-aegis-emerald/10",
};

export default function Deployment({ status }) {
  const s = status || {};

  const copyPrompt = async () => {
    const ok = await copyText(EMERGENT_PROMPT);
    if (ok) toast.success("Deployment prompt copied");
    else toast.error("Copy failed");
  };

  const exportLedger = async () => {
    try {
      const { data } = await http.get("/worm/export");
      downloadJSON(`veralix-aegis-worm-${Date.now()}.json`, data);
      toast.success("WORM ledger exported");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="tab-deploy">
      <div>
        <Overline>Emergent &amp; DevOps Delivery Package</Overline>
        <h1 className="font-heading text-2xl font-light tracking-tight text-slate-100 mt-1">最终生产上线交付套件</h1>
        <p className="text-sm text-slate-400 mt-1">一键部署指令、AWS 基础设施映射清单与不可篡改证据导出。</p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {[
          { k: "AGENTIC_AI_ENABLED", v: "true", c: "text-aegis-emerald" },
          { k: "Environment", v: (s.environment || "staging").toUpperCase(), c: "text-aegis-cyan" },
          { k: "Region", v: s.region || "us-east-1", c: "text-slate-200" },
          { k: "Model Backend", v: "Bedrock Converse", c: "text-aegis-purple" },
        ].map((b) => (
          <div key={b.k} className="rounded-xl border border-edge bg-panel/70 p-4">
            <Overline>{b.k}</Overline>
            <p className={`mt-1.5 font-mono text-sm ${b.c}`}>{b.v}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel icon={Rocket} title="1. Emergent 自动化上线 Prompt" subtitle="Target Architecture — Option 2" accent="cyan"
          actions={<Btn variant="cyan" onClick={copyPrompt} testid="copy-prompt-btn"><Copy size={14} /> Copy</Btn>}
        >
          <pre className="overflow-x-auto rounded-lg border border-edge well p-4 font-mono text-[11.5px] leading-relaxed text-slate-300 no-scrollbar">
{EMERGENT_PROMPT}
          </pre>
        </Panel>

        <Panel icon={Server} title="2. AWS 基础设施映射清单" subtitle="Infrastructure binding map" accent="purple" testid="aws-map-panel">
          <div className="space-y-2.5">
            {AWS_MAP.map((m) => (
              <div key={m.svc} className="flex items-center justify-between gap-3 rounded-lg border border-edge raise px-3 py-2.5">
                <div className="flex items-center gap-3 min-w-0">
                  <Cloud size={15} className="text-aegis-cyan shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium text-slate-100">{m.svc}</p>
                    <p className="text-[11px] text-slate-400 truncate">{m.role}</p>
                  </div>
                </div>
                <span className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-mono uppercase ${STATE_COLOR[m.state]}`}>
                  {m.state}
                </span>
              </div>
            ))}
            <div className="mt-2 flex items-center gap-2 rounded-lg border border-aegis-emerald/25 bg-aegis-emerald/5 px-3 py-2 text-[11px] font-mono text-aegis-emerald">
              <CheckCircle2 size={13} /> VITE_AEGIS_API_GATEWAY_URL → {s.api_gateway_url || "https://aegis-gw…amazonaws.com/v1"}
            </div>
          </div>
        </Panel>
      </div>

      <Panel icon={Download} title="3. 证据日志导出与存证打包" subtitle="Court-ready WORM evidence export" accent="emerald">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <p className="text-sm text-slate-400 max-w-xl">
            导出全量不可篡改 WORM 账本（含 SHA-256 前向哈希链与 Steward 签名），用于链上存证与合规审计交付。
          </p>
          <Btn variant="emerald" onClick={exportLedger} testid="deploy-export-btn">
            <Download size={15} /> 导出不可篡改 WORM 账本 JSON
          </Btn>
        </div>
      </Panel>
    </div>
  );
}
